import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Save, SlidersHorizontal } from "lucide-react";
import { AUTO_LOCK_CAPS, POLICY_MIN_PASSWORD_CEILING, POLICY_MIN_PASSWORD_FLOOR, REMEMBER_DEVICE_DAYS, saveAdminPolicies } from "../../api/policies.js";
import type { AdminPolicies, Policies } from "../../api/policies.js";
import type { AdminUser } from "../../api/admin.js";
import { ADMIN_POLICIES_KEY, ME_KEY, useAdminPolicies, useMe } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Field, SelectInput, Switch, TextInput } from "../../ui/forms.js";
import { Alert, Button, Card, CardHeader, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

function lockLabel(minutes: number): string {
  return minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60} Stunde${minutes === 60 ? "" : "n"}` : `${minutes} Minuten`;
}

/** Admin: instance-wide policies. Loads once, then edits a local draft until saved. */
export function PoliciesPanel(props: { readonly users: ReadonlyArray<AdminUser> | undefined }): ReactNode {
  const policies = useAdminPolicies(true);
  if (policies.isPending) {
    return <Skeleton className="h-72" />;
  }
  if (policies.error !== null) {
    return <Alert tone="danger">{policies.error.message}</Alert>;
  }
  return <PoliciesForm key={policies.data.updatedAt ?? 0} saved={policies.data} users={props.users} />;
}

function PoliciesForm(props: { readonly saved: AdminPolicies; readonly users: ReadonlyArray<AdminUser> | undefined }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [requireTwoFactor, setRequireTwoFactor] = useState(props.saved.requireTwoFactor);
  const [minLength, setMinLength] = useState(String(props.saved.minPasswordLength));
  const [lockCap, setLockCap] = useState(props.saved.maxAutoLockMinutes === null ? "" : String(props.saved.maxAutoLockMinutes));
  const [exportsDisabled, setExportsDisabled] = useState(props.saved.exportsDisabled);
  const [requireAdminRecovery, setRequireAdminRecovery] = useState(props.saved.requireAdminRecovery);
  const [requireTrustedDevices, setRequireTrustedDevices] = useState(props.saved.requireTrustedDevices);
  const [singleActiveSession, setSingleActiveSession] = useState(props.saved.singleActiveSession);
  const [requirePasskey, setRequirePasskey] = useState(props.saved.requirePasskey);
  const [signOutOnLock, setSignOutOnLock] = useState(props.saved.signOutOnLock);
  const [rememberDevices, setRememberDevices] = useState(props.saved.rememberDevices);
  const [rememberDays, setRememberDays] = useState(String(props.saved.rememberDevicesDays));
  const [allowExtensionLink, setAllowExtensionLink] = useState(props.saved.allowExtensionLink);
  const [passkeyProvider, setPasskeyProvider] = useState(props.saved.passkeyProvider);
  const recoveryReady = useMe().data?.adminRecovery !== null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const length = Number(minLength);
  const lengthValid = Number.isInteger(length) && length >= POLICY_MIN_PASSWORD_FLOOR && length <= POLICY_MIN_PASSWORD_CEILING;
  const draft: Policies = {
    requireTwoFactor,
    minPasswordLength: lengthValid ? length : props.saved.minPasswordLength,
    maxAutoLockMinutes: lockCap === "" ? null : Number(lockCap),
    exportsDisabled,
    requireAdminRecovery,
    requireTrustedDevices,
    singleActiveSession,
    requirePasskey,
    signOutOnLock,
    rememberDevices,
    rememberDevicesDays: Number(rememberDays),
    allowExtensionLink,
    passkeyProvider
  };
  const dirty = draft.requireTwoFactor !== props.saved.requireTwoFactor ||
    draft.minPasswordLength !== props.saved.minPasswordLength ||
    draft.maxAutoLockMinutes !== props.saved.maxAutoLockMinutes ||
    draft.exportsDisabled !== props.saved.exportsDisabled ||
    draft.requireAdminRecovery !== props.saved.requireAdminRecovery ||
    draft.requireTrustedDevices !== props.saved.requireTrustedDevices ||
    draft.singleActiveSession !== props.saved.singleActiveSession ||
    draft.requirePasskey !== props.saved.requirePasskey ||
    draft.signOutOnLock !== props.saved.signOutOnLock ||
    draft.rememberDevices !== props.saved.rememberDevices ||
    draft.rememberDevicesDays !== props.saved.rememberDevicesDays ||
    draft.allowExtensionLink !== props.saved.allowExtensionLink ||
    draft.passkeyProvider !== props.saved.passkeyProvider;
  const withoutTwoFactor = (props.users ?? []).filter((u: AdminUser): boolean => !u.twoFactorEnabled && u.status !== "disabled").length;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (!lengthValid) {
      setError(`Die Mindestlänge muss zwischen ${POLICY_MIN_PASSWORD_FLOOR} und ${POLICY_MIN_PASSWORD_CEILING} Zeichen liegen.`);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const saved = await saveAdminPolicies(draft);
      queryClient.setQueryData<AdminPolicies>(ADMIN_POLICIES_KEY, saved);
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      toast.notify("Richtlinien gespeichert. Sie gelten sofort für alle Konten.", { tone: "success" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Die Richtlinien konnten nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        icon={SlidersHorizontal}
        title="Richtlinien"
        description="Gelten für alle Konten dieser Instanz, auf jedem Gerät, sobald sie gespeichert sind."
      />
      <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 px-5 py-4" noValidate>
        <div className="flex flex-col gap-2">
          <Switch
            label="Zwei-Faktor-Anmeldung verlangen"
            description="Konten ohne Authenticator-App oder Passkey kommen nur noch in die Einstellungen, bis sie einen zweiten Faktor einrichten. Den letzten Faktor kann niemand mehr entfernen."
            checked={requireTwoFactor}
            onChange={setRequireTwoFactor}
            disabled={busy}
          />
          {requireTwoFactor && withoutTwoFactor > 0 ? (
            <Alert tone="info">
              {withoutTwoFactor === 1 ? "1 aktives Konto hat" : `${withoutTwoFactor} aktive Konten haben`} noch keinen zweiten Faktor und {withoutTwoFactor === 1 ? "muss" : "müssen"} ihn bei der nächsten Nutzung einrichten.
            </Alert>
          ) : null}
        </div>

        <Field
          id="policy-min-length"
          label="Mindestlänge des Master-Passworts"
          hint={`${POLICY_MIN_PASSWORD_FLOOR} bis ${POLICY_MIN_PASSWORD_CEILING} Zeichen. Die Apps prüfen sie beim Festlegen und beim Entsperren; wer kürzer ist, muss sein Passwort ändern. Der Server sieht das Passwort nie.`}
        >
          <TextInput
            id="policy-min-length"
            type="number"
            inputMode="numeric"
            min={POLICY_MIN_PASSWORD_FLOOR}
            max={POLICY_MIN_PASSWORD_CEILING}
            value={minLength}
            invalid={!lengthValid}
            disabled={busy}
            onChange={(event): void => setMinLength(event.target.value)}
            className="sm:w-32"
          />
        </Field>

        <Field id="policy-lock-cap" label="Längste automatische Sperre" hint="Nutzer können nur kürzere Zeiten wählen – in Web-App, Desktop-App und Browser-Erweiterung.">
          <SelectInput id="policy-lock-cap" value={lockCap} disabled={busy} onChange={(event): void => setLockCap(event.target.value)}>
            <option value="">Keine Begrenzung</option>
            {AUTO_LOCK_CAPS.map((minutes: number) => (
              <option key={minutes} value={String(minutes)}>
                Höchstens {lockLabel(minutes)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Switch
          label="Export sperren"
          description="Der Server verweigert den Export des Tresors als unverschlüsselte Datei. Einträge bleiben für berechtigte Nutzer weiterhin sichtbar."
          checked={exportsDisabled}
          onChange={setExportsDisabled}
          disabled={busy}
        />

        <div className="flex flex-col gap-2">
          <Switch
            label="Admin-Wiederherstellung für alle Konten"
            description="Jede App registriert ihr Konto beim nächsten Entsperren für die Admin-Wiederherstellung; abmelden ist dann nicht mehr möglich. Ohne diese Richtlinie entscheidet jedes Konto selbst."
            checked={requireAdminRecovery}
            onChange={setRequireAdminRecovery}
            disabled={busy}
          />
          {requireAdminRecovery && !recoveryReady ? (
            <Alert tone="info">Wirkt erst, wenn im Bereich „Wiederherstellung“ der Schlüssel der Instanz eingerichtet ist.</Alert>
          ) : null}
        </div>

        <Switch
          label="Neue Geräte freigeben lassen"
          description="Ein neues Gerät braucht neben dem Master-Passwort einen Passkey oder die Freigabe von einem bereits vertrauten Gerät des Kontos."
          checked={requireTrustedDevices}
          onChange={setRequireTrustedDevices}
          disabled={busy}
        />

        <Switch
          label="Nur eine aktive Sitzung"
          description="Eine neue Anmeldung beendet alle anderen Sitzungen des Kontos."
          checked={singleActiveSession}
          onChange={setSingleActiveSession}
          disabled={busy}
        />

        <Switch
          label="Beim Sperren abmelden"
          description="Sperren beendet auch die Sitzung der Browser-Erweiterung. Jedes Entsperren ist dann eine volle Anmeldung mit E-Mail, Master-Passwort und zweitem Faktor."
          checked={signOutOnLock}
          onChange={setSignOutOnLock}
          disabled={busy}
        />

        <div className="flex flex-col gap-3">
          <Switch
            label="Geräte merken erlauben"
            description="Die Browser-Erweiterung kann sich nach einer Anmeldung mit zweitem Faktor merken: Danach reicht dort das Master-Passwort zusammen mit dem Geräteschlüssel. Abgeschaltet verlangt jede Anmeldung wieder den zweiten Faktor."
            checked={rememberDevices}
            onChange={setRememberDevices}
            disabled={busy}
          />
          {rememberDevices ? (
            <Field id="policy-remember-days" label="Gerät gemerkt für" hint="Danach ist der zweite Faktor wieder fällig. Gilt auch für bereits gemerkte Geräte.">
              <SelectInput id="policy-remember-days" value={rememberDays} disabled={busy} onChange={(event): void => setRememberDays(event.target.value)} className="sm:w-48">
                {REMEMBER_DEVICE_DAYS.map((days: number) => (
                  <option key={days} value={String(days)}>
                    {days} Tage
                  </option>
                ))}
              </SelectInput>
            </Field>
          ) : null}
        </div>

        <Switch
          label="Erweiterung per Passkey verbinden erlauben"
          description="Die Browser-Erweiterung kann sich über diese Web-App anmelden: Nach Passkey-Bestätigung und Eingabe des Codes aus der Erweiterung bekommt sie Sitzung und Kontoschlüssel verschlüsselt übergeben. Abgeschaltet meldet sich die Erweiterung nur mit Master-Passwort an."
          checked={allowExtensionLink}
          onChange={setAllowExtensionLink}
          disabled={busy}
        />

        <Switch
          label="Passkey-Anbieter in der Erweiterung erlauben"
          description="Die Browser-Erweiterung kann Passkeys für Websites aus dem Tresor anbieten und neue dort speichern. Noch in Erprobung: erst nach dem abschließenden Sicherheitstest einschalten. Gespeicherte Passkeys bleiben in jedem Fall in den Einträgen."
          checked={passkeyProvider}
          onChange={setPasskeyProvider}
          disabled={busy}
        />

        <Switch
          label="Passkey als zweiten Faktor verlangen"
          description="Phishing-resistent: Jedes Konto braucht einen Passkey und meldet sich damit an. Codes aus der Authenticator-App reichen dann nicht mehr."
          checked={requirePasskey}
          onChange={setRequirePasskey}
          disabled={busy}
        />

        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <p className="text-[0.8125rem] text-mist-2">
            {props.saved.updatedAt === null ? "Noch nie geändert – es gelten die Standardwerte." : (
              <>
                Zuletzt geändert <span title={formatDateTime(props.saved.updatedAt)}>{formatRelativeTime(props.saved.updatedAt, Date.now())}</span>
                {props.saved.updatedByEmail !== null ? ` von ${props.saved.updatedByEmail}` : ""}.
              </>
            )}
          </p>
          <Button type="submit" icon={Save} loading={busy} disabled={!dirty}>
            Speichern
          </Button>
        </div>
      </form>
    </Card>
  );
}
